/**
 * MDD (Meter Data Dictionary) Parser
 *
 * Parses EnergyPlus .mdd files which contain available meters
 * for a given model. The MDD file is generated after a simulation and lists
 * all reportable meters.
 *
 * Format example:
 * Output:Meter,Electricity:Facility,hourly; !- [J]
 * Output:Meter,DistrictHeating:Facility,hourly; !- [J]
 * Output:Meter:MeterFileOnly,Gas:Plant,hourly; !- [J]
 */

import type { ReportFrequency } from './types';

/**
 * Represents an available meter from the MDD file
 */
export interface MDDMeter {
  /** Meter name (e.g., "Electricity:Facility", "Heating:Zone:ZONE1") */
  meterName: string;
  /** Default reporting frequency */
  reportingFrequency: ReportFrequency;
  /** Units (e.g., "J", "W") */
  units: string;
  /** Whether this is a meter-file-only output */
  meterFileOnly: boolean;
  /** Whether this is a cumulative meter */
  cumulative: boolean;
}

/**
 * Parsed MDD file data
 */
export interface MDDData {
  /** List of available meters */
  meters: MDDMeter[];
}

/**
 * Parse report frequency string to enum
 */
function parseReportFrequency(freq: string): ReportFrequency {
  const normalized = freq.toLowerCase().trim();
  switch (normalized) {
    case 'timestep':
      return 'timestep';
    case 'hourly':
      return 'hourly';
    case 'daily':
      return 'daily';
    case 'monthly':
      return 'monthly';
    case 'runperiod':
      return 'runperiod';
    case 'environment':
      return 'environment';
    case 'annual':
      return 'annual';
    default:
      return 'hourly';
  }
}

/**
 * Parse a single MDD line in IDF format
 * Format: Output:Meter,MeterName,frequency; !- [units]
 * or: Output:Meter:MeterFileOnly,MeterName,frequency; !- [units]
 * or: Output:Meter:Cumulative,MeterName,frequency; !- [units]
 */
function parseMDDLineIDF(line: string): MDDMeter | null {
  const trimmed = line.trim();
  const lowerTrimmed = trimmed.toLowerCase();
  if (!lowerTrimmed.startsWith('output:meter')) {
    return null;
  }

  // Extract units from comment if present
  let units = '';
  const unitsMatch = trimmed.match(/!\s*-?\s*\[([^\]]*)\]/);
  if (unitsMatch?.[1]) {
    units = unitsMatch[1];
  }

  // Determine meter type flags
  const meterFileOnly = lowerTrimmed.includes('meterfileonly');
  const cumulative = lowerTrimmed.includes('cumulative');

  // Remove the trailing comment for parsing
  const mainPart = trimmed.split('!')[0]?.trim() ?? '';

  // Remove trailing semicolon
  const withoutSemicolon = mainPart.replace(/;$/, '').trim();

  // Split by comma
  const parts = withoutSemicolon.split(',').map((p) => p.trim());

  // Expected format: Output:Meter[:variant], meterName, frequency
  if (parts.length < 3) {
    return null;
  }

  const meterName = parts[1] ?? '';
  const frequency = parts[2] ?? 'hourly';

  if (!meterName) {
    return null;
  }

  return {
    meterName,
    reportingFrequency: parseReportFrequency(frequency),
    units,
    meterFileOnly,
    cumulative,
  };
}

/**
 * Parse a single MDD line in CSV/Regular format
 * Format: Meter Name [Units]
 * Example: Electricity:Facility [J]
 */
function parseMDDLineCSV(line: string): MDDMeter | null {
  const trimmed = line.trim();
  if (!trimmed) return null;

  // Skip header lines
  if (trimmed.startsWith('Program Version') ||
      trimmed.startsWith('Meter Type') ||
      trimmed.startsWith('Cumulative Meter') ||
      trimmed.startsWith('!') ||
      trimmed.toLowerCase().includes('report type') ||
      trimmed.toLowerCase().includes('meter name')) {
    return null;
  }

  // MDD CSV format appears to be simpler: just "MeterName [Units]"
  // But may also have columns like RDD. Let's handle both cases.

  // Try to extract meter name and units
  let meterName = trimmed;
  let units = '';

  // Check if it's a comma-separated format first
  if (trimmed.includes(',')) {
    const parts = trimmed.split(',').map(p => p.trim());
    // Could be "Type,MeterName [Units]" format
    if (parts.length >= 2) {
      // Last part is likely the meter name with units
      const lastPart = parts[parts.length - 1] ?? '';
      const unitsMatch = lastPart.match(/^(.+?)\s*\[([^\]]*)\]\s*$/);
      if (unitsMatch) {
        meterName = unitsMatch[1]?.trim() ?? lastPart;
        units = unitsMatch[2] ?? '';
      } else {
        meterName = lastPart;
      }
    }
  } else {
    // Simple format: "MeterName [Units]"
    const unitsMatch = trimmed.match(/^(.+?)\s*\[([^\]]*)\]\s*$/);
    if (unitsMatch) {
      meterName = unitsMatch[1]?.trim() ?? trimmed;
      units = unitsMatch[2] ?? '';
    }
  }

  if (!meterName) {
    return null;
  }

  return {
    meterName,
    reportingFrequency: 'hourly',
    units,
    meterFileOnly: false,
    cumulative: false,
  };
}

/**
 * Detect the MDD file format
 */
function detectMDDFormat(content: string): 'idf' | 'csv' {
  const lines = content.split('\n').slice(0, 10);
  for (const line of lines) {
    if (line.trim().toLowerCase().startsWith('output:meter')) {
      return 'idf';
    }
  }
  return 'csv';
}

/**
 * Parse MDD file content
 * Automatically detects format (IDF-style or CSV-style)
 */
export function parseMDD(content: string): MDDData {
  const lines = content.split('\n');
  const meters: MDDMeter[] = [];

  const format = detectMDDFormat(content);
  const parser = format === 'idf' ? parseMDDLineIDF : parseMDDLineCSV;

  for (const line of lines) {
    const meter = parser(line);
    if (meter) {
      meters.push(meter);
    }
  }

  return { meters };
}

/**
 * Get unique meter names from MDD data
 */
export function getUniqueMeterNames(data: MDDData): string[] {
  const names = new Set<string>();
  for (const meter of data.meters) {
    names.add(meter.meterName);
  }
  return Array.from(names).sort();
}
